import { BackendQnAService } from './BackendQnAService';
import {
  AIService,
  AIMessage,
  AIResponse,
  AIServiceConfig,
  AIServiceError,
  StreamingAIService,
  StreamingAIResponse,
} from './types';

export type AIProvider = 'backend';

export interface AIServiceOptions {
  provider: AIProvider;
  config?: Partial<AIServiceConfig>;
}

export class QnAAIServiceManager {
  private currentService: AIService;
  private provider: AIProvider;

  constructor(options: AIServiceOptions) {
    this.provider = options.provider;
    this.currentService = this.createService(options);
  }

  private createService(options: AIServiceOptions): AIService {
    switch (options.provider) {
      case 'backend': {
        return new BackendQnAService(
          options.config?.overrideParams,
          options.config?.timeout || 120000,
        );
      }
      default:
        throw new AIServiceError(
          `Unknown AI provider: ${options.provider}`,
          'CONFIG_ERROR',
          false,
        );
    }
  }

  getProviderName(): string {
    return this.currentService.getProviderName();
  }

  async generateResponse(messages: AIMessage[], context?: string): Promise<AIResponse> {
    if (!this.currentService.validateConfig()) {
      throw new AIServiceError(
        `Invalid configuration for ${this.getProviderName()}`,
        'CONFIG_ERROR',
        false,
      );
    }

    try {
      return await this.currentService.generateResponse(messages, context);
    } catch (error) {
      if (error instanceof AIServiceError) {
        throw error;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new AIServiceError(
        `Service error: ${errorMessage}`,
        'SERVICE_ERROR',
        true,
      );
    }
  }

  isConfigured(): boolean {
    return this.currentService.validateConfig();
  }

  async generateStreamingResponse(
    messages: AIMessage[],
    context?: string,
    onChunk?: (chunk: StreamingAIResponse) => void,
  ): Promise<AIResponse> {
    if (!this.currentService.validateConfig()) {
      throw new AIServiceError(
        `Invalid configuration for ${this.getProviderName()}`,
        'CONFIG_ERROR',
        false,
      );
    }

    // Check if service supports streaming
    if ('generateStreamingResponse' in this.currentService) {
      const streamingService = this.currentService as StreamingAIService;
      try {
        return await streamingService.generateStreamingResponse(messages, context, onChunk);
      } catch (error) {
        if (error instanceof AIServiceError) {
          throw error;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        throw new AIServiceError(
          `Streaming service error: ${errorMessage}`,
          'SERVICE_ERROR',
          true,
        );
      }
    }

    // Fallback to regular response
    return this.generateResponse(messages, context);
  }
}
